import { useMerchantStore } from '@stores/merchantStore';
import { useBiometricsStore } from '@stores/biometricsStore';
import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { TonWalletUtils } from '@lib/secrets';
import { useReferralBannerStore } from '@stores/referralBannerStore';
import { usePinRetryStore } from '@stores/pinRetryStore';
import { useActivityStore } from '@stores/activityStore';
import { usePaymentLinkActivityStore } from '@stores/paymentLinkActivityStore';
import { usePayLinkStore } from '@stores/payLinkStore';
import { useUserRestrictionsStore } from '@stores/userRestrictionsStore';
import { DeviceStorage, onEvent, offEvent } from '../lib/telegram';
import * as Sentry from "@sentry/react"

// Define the shape of our authentication context
interface AuthContextType {
  // Authentication state
  isAuthenticated: boolean;
  remainingAttempts: number;
  isLocked: boolean;
  isInactive: boolean;

  // Basic authentication methods
  login: () => void;
  logout: () => Promise<void>;

  // PIN authentication and management methods
  resetAttempts: () => void;
  decrementAttempts: () => void;
  verifyPin: (pin: string) => Promise<boolean>;
  setPin: (pin: string) => Promise<boolean>;
  resetInactivity: () => void;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  remainingAttempts: 3,
  isLocked: false,
  isInactive: false,
  login: () => { },
  logout: async () => { },
  verifyPin: async () => false,
  resetAttempts: () => { },
  decrementAttempts: () => { },
  setPin: async () => false,
  resetInactivity: () => { },
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

// Constants for inactivity tracking
const INACTIVITY_TIMEOUT = 60 * 1000 * 5 // 5 minutes in milliseconds
const LAST_ACTIVE_KEY = 'lastActiveTimestamp';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize authentication state with a default value
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [isInactive, setIsInactive] = useState(false);

  useEffect(() => {
    TonWalletUtils.hasWallet().then((hasWallet) => {
      setIsAuthenticated(hasWallet || false);
    });
  }, []);

  const { fetchRestrictions } = useUserRestrictionsStore()

  // Fetch user restrictions as early as possible after authentication
  // Always invalidate on mount to ensure fresh data on every app open
  useEffect(() => {
    if (isAuthenticated) {
      useUserRestrictionsStore.getState().invalidateCache();
      fetchRestrictions();
    }
  }, [isAuthenticated, fetchRestrictions]);

  // Check for inactivity on app initialization and handle app deactivation
  useEffect(() => {
    if (!isAuthenticated) return;

    // Initial check for inactivity
    const checkInitialInactivity = async () => {
      const lastActiveStr = await DeviceStorage.getItem(LAST_ACTIVE_KEY);
      if (!lastActiveStr) {
        // No last active timestamp, set current time
        await DeviceStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
        return;
      }

      const lastActive = parseInt(lastActiveStr, 10);
      const currentTime = Date.now();
      const inactiveTime = currentTime - lastActive;

      if (inactiveTime > INACTIVITY_TIMEOUT) {
        setIsInactive(true);
      } else {
        // Update the timestamp if not inactive
        await DeviceStorage.setItem(LAST_ACTIVE_KEY, currentTime.toString());
      }
    };

    checkInitialInactivity();

    // Define the event handler functions so we can reference them for cleanup
    const handleDeactivated = async () => {
      // When the app is deactivated, store the last active timestamp
      await DeviceStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    };

    const handleActivated = async () => {
      // Refetch user restrictions when returning from background
      useUserRestrictionsStore.getState().invalidateCache();
      await useUserRestrictionsStore.getState().fetchRestrictions();

      // Get the last active timestamp
      const lastActiveStr = await DeviceStorage.getItem(LAST_ACTIVE_KEY);
      if (!lastActiveStr) return;

      const lastActive = parseInt(lastActiveStr, 10);
      const currentTime = Date.now();
      const inactiveTime = currentTime - lastActive;

      // If we've been inactive for too long, set isInactive to true
      if (inactiveTime > INACTIVITY_TIMEOUT) {
        setIsInactive(true);
      }
    };

    // Register event handlers
    onEvent('deactivated', handleDeactivated);
    onEvent('activated', handleActivated);

    // Cleanup function to remove event handlers when component unmounts
    return () => {
      offEvent('deactivated', handleDeactivated);
      offEvent('activated', handleActivated);
    };
  }, [isAuthenticated, isInactive]);

  // Use the existing pin retry store
  const {
    remainingAttempts,
    decrementAttempts: decrementStoreAttempts,
    resetAttempts: resetStoreAttempts
  } = usePinRetryStore();

  // Check if locked when attempts reach zero
  useEffect(() => {
    if (remainingAttempts <= 0) {
      setIsLocked(true);
    } else {
      setIsLocked(false);
    }
  }, [remainingAttempts]);

  const { resetMerchantInfo } = useMerchantStore()
  const { setBiometrics } = useBiometricsStore()
  const { resetBannerState } = useReferralBannerStore()
  const { clearCache: clearActivityCache } = useActivityStore()
  const { clearCache: clearPaymentLinkActivityCache } = usePaymentLinkActivityStore()
  const { setPayLink } = usePayLinkStore()
  const { clearRestrictions } = useUserRestrictionsStore()

  // Login function - store token in localStorage
  const login = () => {
    setIsAuthenticated(true);
  };

  // Logout function - remove token from localStorage
  const logout = async () => {
    TonWalletUtils.clear()
    TonWalletUtils.clearToken()
    setBiometrics(false)
    resetMerchantInfo()
    setIsAuthenticated(false);
    // Reset banner state when user logs out
    resetBannerState()
    // Clear activity caches when logging out
    clearActivityCache()
    clearPaymentLinkActivityCache()
    // Reset paylink to disabled when logging out
    setPayLink(false)
    // Reset PIN attempts when logging out
    resetStoreAttempts();
    // Clear user restrictions cache when logging out
    await clearRestrictions();
  };

  // PIN Authentication Methods

  // Verify PIN against stored PIN
  const verifyPin = useCallback(async (pin: string): Promise<boolean> => {
    const isValid = await TonWalletUtils.checkPin(pin);
    if (isValid) {
      resetStoreAttempts();
    }
    return isValid || false;
  }, [resetStoreAttempts]);

  // Reset attempt counter (wrapper for the store's resetAttempts)
  const resetAttempts = useCallback(() => {
    resetStoreAttempts();
  }, [resetStoreAttempts]);

  // Decrement attempt counter (wrapper for the store's decrementAttempts)
  const decrementAttempts = useCallback(() => {
    decrementStoreAttempts();
  }, [decrementStoreAttempts]);

  // Create a new PIN
  const setPin = useCallback(async (pin: string): Promise<boolean> => {
    try {
      // Implement based on your app's requirements
      // For example: await TonWalletUtils.setPin(pin);
      await TonWalletUtils.storePin(pin)
      return true;
    } catch (error) {
      Sentry.captureException(error)
      return false;
    }
  }, []);

  // Reset inactivity state and update timestamp
  const resetInactivity = useCallback(async () => {
    // First update the state to ensure UI updates immediately
    setIsInactive(false);
    // Then update the storage (this can happen in the background)
    const currentTime = Date.now().toString();
    try {
      await DeviceStorage.setItem(LAST_ACTIVE_KEY, currentTime);
    } catch (error) {
      Sentry.captureException(error)
    }
    // Double-check that state was updated
    return !isInactive;
  }, [isInactive]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      remainingAttempts,
      isLocked,
      isInactive,
      login,
      logout,
      verifyPin,
      resetAttempts,
      decrementAttempts,
      setPin,
      resetInactivity,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
